export interface ReceiptTokenView {
    type: string;
    token: string;
    units?: string;
    unit?: string;
}

export interface TokenHelp {
    title: string;
    description: string;
    enterHint?: string;
}

export type TokenDisplayGroup =
    | { kind: "kct"; tokens: ReceiptTokenView[] }
    | { kind: "single"; token: ReceiptTokenView };

export function isKctToken(type: string): boolean {
    const lower = type.trim().toLowerCase();
    return lower.includes("kct") || lower.includes("key change");
}

export function getKctStep(type: string): number {
    const match = type.trim().match(/^(\d+)\./);
    return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
}

export function extractSupplierCode(type: string): string | undefined {
    const match = type.trim().match(/(?:to|from|for)\s+(\d+)/i);
    return match?.[1];
}

export function groupReceiptTokens(tokens: ReceiptTokenView[]): TokenDisplayGroup[] {
    const kctTokens = tokens
        .filter((t) => isKctToken(t.type))
        .sort((a, b) => getKctStep(a.type) - getKctStep(b.type));
    const otherTokens = tokens.filter((t) => !isKctToken(t.type));

    const groups: TokenDisplayGroup[] = [];
    if (kctTokens.length > 0) {
        groups.push({ kind: "kct", tokens: kctTokens });
    }
    for (const token of otherTokens) {
        groups.push({ kind: "single", token });
    }
    return groups;
}

export function getKctGroupHelp(tokens: ReceiptTokenView[]): { description: string; enterHint: string } {
    const supplierCode =
        tokens.map((t) => extractSupplierCode(t.type)).find(Boolean) ||
        undefined;

    const description = supplierCode
        ? `These update your meter for supplier code ${supplierCode} before your purchase can be loaded. They do not add units to your meter.`
        : "These update your meter when supplier or security settings have changed. They do not add units to your meter.";

    const enterHint =
        tokens.length === 1
            ? "Enter this first, then your purchase token below."
            : `Enter token 1 first, then token 2${tokens.length > 2 ? ", and any further key-change tokens" : ""}, then your purchase token below.`;

    return { description, enterHint };
}

export function getTokenHelp(type: string, hasKctGroup: boolean): TokenHelp {
    const label = type.trim();
    const lower = label.toLowerCase();

    if (lower === "purchased") {
        return {
            title: "Purchase token",
            description: "Adds the units you paid for to your meter.",
            enterHint: hasKctGroup
                ? "Enter this last, after both key-change tokens above."
                : "Enter this on your meter keypad.",
        };
    }

    if (isKctToken(label)) {
        const step = getKctStep(label);
        const supplierCode = extractSupplierCode(label);
        return {
            title: step < Number.MAX_SAFE_INTEGER ? `Key change token ${step}` : "Key change token",
            description: supplierCode
                ? `Updates your meter for supplier code ${supplierCode}. This does not add units.`
                : "Updates your meter when supplier or security settings have changed. This does not add units.",
            enterHint: "Enter before your purchase token.",
        };
    }

    if (lower.includes("clear credit")) {
        return {
            title: "Clear credit token",
            description: "Resets the credit on your meter. Only use if you were instructed to by support.",
            enterHint: "Do not enter unless support told you to.",
        };
    }

    if (lower.includes("clear tamper") || lower.includes("tamper")) {
        return {
            title: "Clear tamper token",
            description: "Clears a tamper or fault condition on your meter.",
            enterHint: "Only use if support instructed you to enter this.",
        };
    }

    if (lower.includes("free") || lower.includes("issue")) {
        return {
            title: label,
            description: "Complimentary units added to your meter at no charge.",
            enterHint: "Enter on your meter keypad.",
        };
    }

    return {
        title: label,
        description: "Enter this token on your meter keypad.",
    };
}

export function getKctTokenLabel(type: string, index: number): string {
    const step = getKctStep(type);
    if (step < Number.MAX_SAFE_INTEGER) {
        return `Key change token ${step}`;
    }
    return `Key change token ${index + 1}`;
}
